/**
 * Video Preloader Utility
 *
 * Preloads video URLs into the browser cache before switching overlay sources.
 * This ensures smooth transitions from blob URLs to Convex URLs without playback interruption.
 *
 * The preloader:
 * 1. Creates a hidden video element
 * 2. Loads the video until enough data is available for playback
 * 3. Returns once the browser has cached the initial video data
 * 4. Cleans up the hidden element
 *
 * This approach ensures Convex URLs are already in browser cache when overlays switch,
 * providing performance nearly identical to blob URLs.
 */

/**
 * Preloads a video URL into browser cache by creating a hidden video element
 * and loading enough data for smooth playback
 *
 * @param url - The video URL to preload (typically a Convex URL)
 * @param timeout - Maximum time to wait for preloading (default: 5000ms)
 * @returns Promise that resolves when video is cached or rejects on error
 */
export const preloadVideoUrl = async (url: string, timeout: number = 5000): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Validate URL
    if (!url || typeof url !== 'string') {
      console.warn('[Preloader] Invalid URL provided');
      resolve(); // Don't block on invalid URLs
      return;
    }

    // Skip blob URLs - they're already in memory
    if (url.startsWith('blob:')) {
      console.log('[Preloader] Skipping blob URL (already in memory)');
      resolve();
      return;
    }

    console.log(`[Preloader] Starting preload for: ${url.substring(0, 80)}...`);

    const video = document.createElement('video');
    video.preload = 'auto';
    video.style.display = 'none';
    video.muted = true; // Mute to avoid autoplay restrictions
    video.playsInline = true;

    let isResolved = false;

    const cleanup = () => {
      if (!isResolved) {
        isResolved = true;
        video.removeEventListener('canplay', onCanPlay);
        video.removeEventListener('canplaythrough', onCanPlayThrough);
        video.removeEventListener('loadeddata', onLoadedData);
        video.removeEventListener('error', onError);
        // Pause and clear source to release resources
        video.pause();
        video.src = '';
        video.load();
        video.remove();
      }
    };

    const onCanPlay = () => {
      console.log(`[Preloader] ✓ Video can play (cached): ${url.substring(0, 50)}...`);
      cleanup();
      resolve();
    };

    const onCanPlayThrough = () => {
      console.log(`[Preloader] ✓ Video can play through (fully cached): ${url.substring(0, 50)}...`);
      cleanup();
      resolve();
    };

    const onLoadedData = () => {
      console.log(`[Preloader] ✓ Video data loaded: ${url.substring(0, 50)}...`);
      // Wait a bit more for caching before resolving
      setTimeout(() => {
        cleanup();
        resolve();
      }, 100);
    };

    const onError = (e: Event | string) => {
      console.error(`[Preloader] ✗ Failed to preload video: ${url.substring(0, 50)}...`, e);
      cleanup();
      // Resolve anyway - don't block the transition
      resolve();
    };

    // Set timeout to avoid hanging indefinitely
    const timeoutId = setTimeout(() => {
      console.warn(`[Preloader] ⚠ Preload timeout (${timeout}ms), proceeding anyway: ${url.substring(0, 50)}...`);
      cleanup();
      resolve(); // Resolve anyway after timeout
    }, timeout);

    // Add cleanup of timeout to the cleanup function
    const originalCleanup = cleanup;
    const enhancedCleanup = () => {
      clearTimeout(timeoutId);
      originalCleanup();
    };

    video.addEventListener('canplay', () => {
      clearTimeout(timeoutId);
      onCanPlay();
    });
    video.addEventListener('canplaythrough', () => {
      clearTimeout(timeoutId);
      onCanPlayThrough();
    });
    video.addEventListener('loadeddata', () => {
      clearTimeout(timeoutId);
      onLoadedData();
    });
    video.addEventListener('error', (e) => {
      clearTimeout(timeoutId);
      onError(e);
    });

    // Start loading
    video.src = url;
    video.load();
  });
};

/**
 * Preloads multiple video URLs in parallel
 *
 * @param urls - Array of video URLs to preload
 * @param options - Preload options
 * @returns Promise that resolves when all videos are cached (or failed)
 */
export const preloadMultipleVideos = async (
  urls: string[],
  options: { timeout?: number; concurrency?: number } = {}
): Promise<{ url: string; success: boolean }[]> => {
  const { timeout = 5000, concurrency = 3 } = options;

  const results: { url: string; success: boolean }[] = [];

  // Process in batches based on concurrency
  for (let i = 0; i < urls.length; i += concurrency) {
    const batch = urls.slice(i, i + concurrency);

    const batchResults = await Promise.all(
      batch.map(async (url) => {
        try {
          await preloadVideoUrl(url, timeout);
          return { url, success: true };
        } catch {
          return { url, success: false };
        }
      })
    );

    results.push(...batchResults);
  }

  return results;
};

/**
 * Check if a video URL is likely cached by the browser
 *
 * This is a heuristic check - browsers don't provide direct cache access.
 * We check if the URL can be loaded quickly (suggesting it's cached).
 *
 * @param url - The video URL to check
 * @returns Promise<boolean> - True if likely cached
 */
export const isVideoCached = async (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!url || url.startsWith('blob:')) {
      resolve(true); // Blob URLs are always "cached" (in memory)
      return;
    }

    const video = document.createElement('video');
    video.preload = 'metadata';
    video.style.display = 'none';

    const startTime = Date.now();
    const FAST_LOAD_THRESHOLD = 200; // ms - if loads faster than this, likely cached

    const cleanup = () => {
      video.removeEventListener('loadedmetadata', onLoad);
      video.removeEventListener('error', onError);
      video.src = '';
      video.remove();
    };

    const onLoad = () => {
      const loadTime = Date.now() - startTime;
      cleanup();
      resolve(loadTime < FAST_LOAD_THRESHOLD);
    };

    const onError = () => {
      cleanup();
      resolve(false);
    };

    // Timeout after 1 second
    setTimeout(() => {
      cleanup();
      resolve(false);
    }, 1000);

    video.addEventListener('loadedmetadata', onLoad);
    video.addEventListener('error', onError);
    video.src = url;
    video.load();
  });
};
